import { useLocalization } from '@ais-ui/locale-management';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Separator,
  Switch,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  SourceTypes,
  TargetFormValues,
  TargetCardTargetType,
  AVAILABLE_VESSEL_FIELDS,
  AVAILABLE_ATON_FIELDS,
  AVAILABLE_VIRTUAL_ATON_FIELDS,
  AVAILABLE_METHYD_FIELDS,
  AVAILABLE_BASESTATION_FIELDS,
  AVAILABLE_RADAR_FIELDS,
  AVAILABLE_DAS_FIELDS,
  AVAILABLE_DRONE_FIELDS,
} from './useTargetForm';
import { cn } from '@ais-ui/utils';
import { TargetCardSettingsTabs } from './TargetCardSettingsTabs';
import { getFieldDisplayName, getTargetTypeTitle } from './fieldHelpers';

// Get available fields for the given target type
const getAvailableFields = (targetType: TargetCardTargetType): readonly string[] => {
  switch (targetType) {
    case 'vessel':
      return AVAILABLE_VESSEL_FIELDS;
    case 'aton':
      return AVAILABLE_ATON_FIELDS;
    case 'virtualAton':
      return AVAILABLE_VIRTUAL_ATON_FIELDS;
    case 'methyd':
      return AVAILABLE_METHYD_FIELDS;
    case 'baseStation':
      return AVAILABLE_BASESTATION_FIELDS;
    case 'radar':
      return AVAILABLE_RADAR_FIELDS;
    case 'das':
      return AVAILABLE_DAS_FIELDS;
    case 'drone':
      return AVAILABLE_DRONE_FIELDS;
    default:
      return AVAILABLE_VESSEL_FIELDS;
  }
};

// Interface for timeout form item props
interface SourceTypeTimeouts {
  deleted: number;
  dst: number;
  lost: number;
}

interface TimeoutFormItemProps {
  sourceTypeTimeout: SourceTypeTimeouts;
  form: UseFormReturn<TargetFormValues>;
  index: number;
}

const TimeoutFormItem = ({
  sourceTypeTimeout,
  form,
  index,
}: TimeoutFormItemProps) => {
  const { messages } = useLocalization();
  let sourceTypeLabel = '';
  let sourceTypeInfoIconText = '';

  if (sourceTypeTimeout.dst === SourceTypes.ais) {
    sourceTypeLabel = messages.settingsTargetLabelAisLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.radar) {
    sourceTypeLabel = messages.settingsTargetLabelRadarLabel;
    sourceTypeInfoIconText = messages.settingsTargetRadarInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.lrit) {
    sourceTypeLabel = messages.settingsTargetLabelLritLabel;
    sourceTypeInfoIconText = messages.settingsTargetLritInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.aisSat) {
    sourceTypeLabel = messages.settingsTargetLabelAisSatLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisSatInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.adsB) {
    sourceTypeLabel = messages.settingsTargetLabelAdsBLabel;
    sourceTypeInfoIconText = messages.settingsTargetAdsBInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.pps) {
    sourceTypeLabel = messages.settingsTargetLabelPPSLabel;
    sourceTypeInfoIconText = messages.settingsTargetPPSInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.land) {
    sourceTypeLabel = messages.settingsTargetLabelLandLabel;
    sourceTypeInfoIconText = messages.settingsTargetLandInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.aisVessel) {
    sourceTypeLabel = messages.settingsTargetLabelAisVesselLabel;
    sourceTypeInfoIconText = messages.settingsTargetAisVesselInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.das) {
    sourceTypeLabel = messages.settingsTargetLabelDASLabel;
    sourceTypeInfoIconText = messages.settingsTargetDASInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.droneSensor) {
    sourceTypeLabel = messages.settingsTargetLabelDroneSensorLabel;
    sourceTypeInfoIconText = messages.settingsTargetDroneSensorInfoIconLabel;
  } else if (sourceTypeTimeout.dst === SourceTypes.unspecified) {
    sourceTypeLabel = messages.settingsTargetLabelUnspecifiedLabel;
    sourceTypeInfoIconText = messages.settingsTargetUnspecifiedInfoIconLabel;
  }

  // Get errors for this specific timeout item
  const lostError = form.formState.errors.targetTimeouts?.[index]?.lost;
  const removedError = form.formState.errors.targetTimeouts?.[index]?.deleted;
  const hasError = lostError || removedError;

  return (
    <FormItem
      className="flex flex-row border rounded-lg p-2 pr-4 pl-4 pb-4 items-center"
      key={sourceTypeLabel}
    >
      <div className="flex flex-col">
        <div className="flex">
          <h3 className="text-lg font-medium">{sourceTypeLabel}</h3>
          <InfoIconTooltip className="mb-4 ml-1">
            {sourceTypeInfoIconText}
          </InfoIconTooltip>
        </div>
        {hasError && (
          <p className={cn('text-[0.8rem] font-medium text-destructive')}>
            {lostError?.message || removedError?.message}
          </p>
        )}
      </div>

      <div className="flex flex-col flex-grow">
        <div className="flex justify-end space-x-2 items-center">
          <p className="text-sm text-muted-foreground">
            {messages.settingsTargetTimeoutLost}
          </p>
          <FormField
            key={`${SourceTypes[sourceTypeTimeout.dst]}TargetLostKey`}
            control={form.control}
            name={`targetTimeouts.${index}.lost`}
            render={({ field }) => (
              <FormControl>
                <Input
                  type="number"
                  className="w-20"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseInt(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
            )}
          />
          <p className="text-sm text-muted-foreground">
            {messages.settingsTargetTimeoutRemoved}
          </p>
          <FormField
            key={`${SourceTypes[sourceTypeTimeout.dst]}TargetDeletedKey`}
            control={form.control}
            name={`targetTimeouts.${index}.deleted`}
            render={({ field }) => (
              <FormControl>
                <Input
                  type="number"
                  className="w-20"
                  {...field}
                  onChange={(e) => {
                    if (!e.target.value) {
                      field.onChange('');
                    } else {
                      const numberValue = parseInt(e.target.value);
                      field.onChange(numberValue);
                    }
                  }}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
    </FormItem>
  );
};

interface TargetFormProps {
  form: UseFormReturn<TargetFormValues>;
}

export const TargetForm = ({ form }: TargetFormProps) => {
  const { messages } = useLocalization();
  const [targetTypeTab, setTargetTypeTab] = useState<TargetCardTargetType>('vessel');
  const availableFields = getAvailableFields(targetTypeTab);

  return (
    <Form {...form}>
      <div className="space-y-12">
        {/* Scale Settings Section */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium">Scale & Tracking Settings</h3>
            <p className="text-sm text-muted-foreground">
              Configure map scale and tracking duration settings.
            </p>
          </div>
          <div className="space-y-4">
            <FormField
              key="targetScale"
              control={form.control}
              name="targetScale"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackScaleLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackScaleDescription}
                    </FormDescription>
                  </div>
                  <FormControl
                    id="settings-target-scale"
                    onChange={() => form.clearErrors()}
                  >
                    <Input
                      className="w-20"
                      min={75}
                      max={325}
                      type="number"
                      {...field}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange('');
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              key="predictorDuration"
              control={form.control}
              name="predictorDuration"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackPredictorDurationLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackPredictorDurationDescription}
                    </FormDescription>
                  </div>
                  <FormControl
                    id="settings-target-predictor"
                    onChange={() => form.clearErrors()}
                  >
                    <Input
                      className="w-20"
                      min={1}
                      type="number"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange(null);
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              key="sogVectorDuration"
              control={form.control}
              name="sogVectorDuration"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsMapTrackSOGVectorDurationLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsMapTrackSOGVectorDurationDescription}
                    </FormDescription>
                  </div>
                  <FormControl
                    id="settings-target-sog-vector"
                    onChange={() => form.clearErrors()}
                  >
                    <Input
                      className="w-20"
                      min={1}
                      type="number"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        if (!e.target.value) {
                          field.onChange(null);
                        } else {
                          const numberValue = parseFloat(e.target.value);
                          field.onChange(numberValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Target Card Settings Section */}
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium">Target Card Settings</h3>
              <p className="text-sm text-muted-foreground">
                Configure target card display preferences for {getTargetTypeTitle(targetTypeTab)} targets.
              </p>
            </div>
            <TargetCardSettingsTabs tab={targetTypeTab} setTab={setTargetTypeTab} />
          </div>
          <div className="space-y-4">
            <FormField
              key="targetCardExpandedByDefault"
              control={form.control}
              name="targetCardExpandedByDefault"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Expand target card by default
                    </FormLabel>
                    <FormDescription>
                      When enabled, target cards will be expanded by default
                      instead of minimized.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              key={`favoriteFields-${targetTypeTab}`}
              control={form.control}
              name={`favoriteFields.${targetTypeTab}`}
              render={({ field }) => (
                <FormItem className="border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Favorite fields for minimized view
                    </FormLabel>
                    <FormDescription>
                      Select up to 4 fields to display in the minimized target
                      card view for {getTargetTypeTitle(targetTypeTab)} targets.
                    </FormDescription>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {availableFields.map((targetField) => {
                      const currentValue = field.value || [];
                      const isSelected = currentValue.includes(targetField);
                      const canSelect = currentValue.length < 4 || isSelected;

                      return (
                        <div
                          key={targetField}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`field-${targetTypeTab}-${targetField}`}
                            checked={isSelected}
                            disabled={!canSelect}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([...currentValue, targetField]);
                              } else {
                                field.onChange(
                                  currentValue.filter(
                                    (f: string) => f !== targetField,
                                  ),
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor={`field-${targetTypeTab}-${targetField}`}
                            className={cn(
                              'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                              !canSelect && 'opacity-50',
                            )}
                          >
                            {getFieldDisplayName(targetField)}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              key={`targetHeaderField-${targetTypeTab}`}
              control={form.control}
              name={`targetHeaderField.${targetTypeTab}`}
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Target card header field
                    </FormLabel>
                    <FormDescription>
                      Choose which field to display as the main title in the
                      target card header for {getTargetTypeTitle(targetTypeTab)} targets.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFields.map((targetField) => (
                          <SelectItem key={targetField} value={targetField}>
                            {getFieldDisplayName(targetField)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              key={`targetSubtitleField-${targetTypeTab}`}
              control={form.control}
              name={`targetSubtitleField.${targetTypeTab}`}
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4',
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Target card subtitle field
                    </FormLabel>
                    <FormDescription>
                      Choose which field to display as the subtitle below the
                      main title for {getTargetTypeTitle(targetTypeTab)} targets.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFields.map((targetField) => (
                          <SelectItem key={targetField} value={targetField}>
                            {getFieldDisplayName(targetField)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Timeout Settings Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-1">
            <div>
              <h3 className="text-lg font-medium">
                {messages.settingsTargetTimeoutLabelTitle}
              </h3>
              <div className="text-sm text-muted-foreground">
                {
                  messages.settingsTargetTimeoutLabelDescription.split(
                    '{lostInfoIcon}',
                  )[0]
                }
                {
                  messages.settingsTargetTimeoutLabelDescription
                    .split('{lostInfoIcon}')[1]
                    .split('{deletedInfoIcon}')[0]
                }
                <InfoIconTooltip>
                  {messages.settingsTargetTimeoutDeletedInfoIcon}
                </InfoIconTooltip>
                {
                  messages.settingsTargetTimeoutLabelDescription.split(
                    '{deletedInfoIcon}',
                  )[1]
                }
              </div>
            </div>
          </div>
          <div className="grid gap-4">
            {form.getValues().targetTimeouts.map((sourceTypeTimeout, index) => {
              return (
                <TimeoutFormItem
                  key={sourceTypeTimeout.dst}
                  sourceTypeTimeout={sourceTypeTimeout}
                  form={form}
                  index={index}
                />
              );
            })}
          </div>
        </div>
      </div>
    </Form>
  );
};
